import { useState } from 'react'
import { styled } from '@mui/material'
import { useIntl } from 'react-intl'
import { colorPalette } from '../../../../style/colorPalette'
import { Button } from '../../../../components/buttons'
import { DeleteButton } from '../../../../containers/SingleSheetV2/buttons/DeleteButton'
import AlertDialog from '../../../../components/dialogs/AlertDialog'
import { CloseIcon } from '../../../../components/icons/CloseIcon'

type Props = {
  submitIsActive: boolean
  submit: () => void
  reload: () => void
  // 編集中（未保存の変更あり）か。リロード/閉じる時の破棄確認に使う。
  edited: boolean
  // ダイアログを閉じる（FunctionLayer 表示時のみ）。渡された時だけ × を表示。
  onClose?: () => void
  // 削除（登録画面のみ）。onDelete が渡された時だけ表示。
  onDelete?: () => void
  deleteDisabled?: boolean
  // ログイン（登録画面・既存プロジェクトのみ）。onLogin が渡された時だけ表示。
  onLogin?: () => void
  loginDisabled?: boolean
}

export const ProjectSubHeader = ({
  submitIsActive,
  submit,
  reload,
  edited,
  onClose,
  onDelete,
  deleteDisabled,
  onLogin,
  loginDisabled,
}: Props) => {
  const intl = useIntl()
  // 編集中にリロードを押した場合は破棄確認を挟む。
  const [reloadConfirmOpen, setReloadConfirmOpen] = useState(false)
  const handleReload = () => {
    if (edited) {
      setReloadConfirmOpen(true)
      return
    }
    reload()
  }
  return (
    <SubHeaderRoot>
      {/* ダイアログ表示時は登録ボタンの左に閉じる（×）ボタンを置く。閉じる処理は
          LayerDialog の confirmClose に委譲し、未保存なら確認ダイアログが出る。 */}
      {onClose && <CloseIcon onClose={onClose} size="s" />}
      {/* WBSアイテム単票に合わせ、更新時も「登録」ラベルにする。 */}
      <StyledButton
        variant="filled"
        colorPattern="skyBlue"
        size="s"
        disabled={!submitIsActive}
        onClick={submit}
      >
        {intl.formatMessage({ id: 'projectV2.register' })}
      </StyledButton>
      <StyledButton
        variant="outlined"
        colorPattern="monotone"
        size="s"
        onClick={handleReload}
      >
        {intl.formatMessage({ id: 'reload' })}
      </StyledButton>
      {/* ログインはリロードボタンの右隣に配置する。 */}
      {onLogin && (
        <StyledButton
          variant="outlined"
          colorPattern="skyBlue"
          size="s"
          disabled={loginDisabled}
          onClick={onLogin}
        >
          {intl.formatMessage({ id: 'projectV2.login' })}
        </StyledButton>
      )}
      {onDelete && (
        <RightGroup>
          <DeleteButton
            disabled={!!deleteDisabled}
            confirmMessage={intl.formatMessage({
              id: 'projectV2.delete.confirm',
            })}
            onConfirm={onDelete}
            disabledReason={intl.formatMessage({
              id: 'projectV2.delete.disabledReason',
            })}
          />
        </RightGroup>
      )}
      <AlertDialog
        isOpen={reloadConfirmOpen}
        message={intl.formatMessage({ id: 'projectV2.reload.confirm' })}
        submitButtonTitle={intl.formatMessage({ id: 'reload' })}
        submitHandler={() => {
          setReloadConfirmOpen(false)
          reload()
        }}
        closeButtonTitle={intl.formatMessage({ id: 'dialog.cancel' })}
        closeHandler={() => setReloadConfirmOpen(false)}
      />
    </SubHeaderRoot>
  )
}

const SubHeaderRoot = styled('div')({
  display: 'flex',
  alignItems: 'center',
  width: '100%',
  padding: '6px 12px',
  backgroundColor: colorPalette.monotone[0],
  borderBottom: `1px solid ${colorPalette.monotone[2]}`,
  gap: '8px',
})
// 削除・ログインはツールバー右端へ寄せる。
const RightGroup = styled('div')({
  marginLeft: 'auto',
  display: 'flex',
  gap: '8px',
})
const StyledButton = styled(Button)({
  minWidth: '100px',
})
